import React, { useState, useRef, useCallback, useEffect } from 'react';
import ChatAssistant from './ChatAssistant';

function AudioToggle() {
  const [playing, setPlaying] = useState(false);
  const audioRef = useRef(null);

  const toggle = useCallback(() => {
    if (!audioRef.current) {
      audioRef.current = new Audio('/RiseV1.wav');
      audioRef.current.loop = true;
    }
    if (playing) {
      audioRef.current.pause();
    } else {
      audioRef.current.play();
    }
    setPlaying(!playing);
  }, [playing]);

  return (
    <button
      onClick={toggle}
      aria-label={playing ? 'Pause music' : 'Play music'}
      style={{
        position: 'absolute',
        bottom: 32,
        right: 32,
        width: 56,
        height: 56,
        borderRadius: '50%',
        border: '2px solid rgba(255, 255, 255, 0.7)',
        background: 'rgba(0, 0, 0, 0.3)',
        cursor: 'pointer',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 3,
        padding: 0,
        backdropFilter: 'blur(4px)',
        transition: 'border-color 0.3s, background 0.3s',
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 1)';
        e.currentTarget.style.background = 'rgba(0, 0, 0, 0.45)';
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.7)';
        e.currentTarget.style.background = 'rgba(0, 0, 0, 0.3)';
      }}
    >
      {playing ? (
        /* Sound bars animating */
        <>
          <style>{`
            @keyframes soundbar {
              0%, 100% { transform: scaleY(0.3); }
              50% { transform: scaleY(1); }
            }
          `}</style>
          {[0, 1, 2, 3, 4].map((i) => (
            <span
              key={i}
              style={{
                display: 'inline-block',
                width: 3,
                height: 18,
                borderRadius: 2,
                background: 'white',
                transformOrigin: 'center',
                animation: `soundbar 0.8s ${i * 0.15}s ease-in-out infinite`,
              }}
            />
          ))}
        </>
      ) : (
        /* Sound bars static (paused look) */
        <>
          {[0.4, 0.7, 1, 0.7, 0.4].map((scale, i) => (
            <span
              key={i}
              style={{
                display: 'inline-block',
                width: 3,
                height: 18,
                borderRadius: 2,
                background: 'white',
                transform: `scaleY(${scale})`,
                opacity: 0.5,
              }}
            />
          ))}
        </>
      )}
    </button>
  );
}

const storyParagraphs = [
  <>Tuniverse was founded on April 10, 2018 in Vancouver, BC by <a href="https://ca.linkedin.com/in/slimrouissi" target="_blank" rel="noopener noreferrer" style={{ color: '#7eb8ff', textDecoration: 'underline' }}>Slim Rouissi</a> with a clear mission: help businesses run better through technology.</>,
  'We started in the enterprise world \u2014 consulting on ERP software and building custom mobile apps and websites for clients across Canada and the United States. That hands-on experience working with businesses of every size gave us something invaluable: a deep understanding of what good software looks like from the inside out.',
  <>Over time, that expertise evolved into something bigger. We began channeling everything we{'\u2019'}d learned into building our own products {'\u2014'} each one shaped by years of solving real problems for real companies. <a href="https://www.yourise.ca/" target="_blank" rel="noopener noreferrer" style={{ color: '#7eb8ff', textDecoration: 'underline' }}>Rise</a> reimagines personal finance for Canadians. RiseTrader brings institutional-grade algorithmic trading to independent traders. KeySnap streamlines rental property management with intuitive inventory tracking and seamless check-in, check-out, and review workflows. Tarot VR brings the mystique of tarot to life on Oculus, using AI to weave personalized, immersive story experiences.</>,
  'From consulting to product development, one thing has never changed: our commitment to software that\u2019s beautifully designed, thoughtfully engineered, and built to make a difference.',
  'We\u2019re just getting started.',
];

function OurStory() {
  const [open, setOpen] = useState(false);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (open) {
      requestAnimationFrame(() => setVisible(true));
    } else {
      setVisible(false);
    }
  }, [open]);

  const handleClose = () => {
    setVisible(false);
    setTimeout(() => setOpen(false), 600);
  };

  return (
    <>
      <div style={{ position: 'relative', marginTop: 10, display: 'inline-block' }}>
        <button
          onClick={() => setOpen(true)}
          style={{
            fontSize: '15px',
            fontWeight: 600,
            letterSpacing: 2,
            background: 'none',
            border: 'none',
            color: 'white',
            cursor: 'pointer',
            padding: 0,
            fontFamily: 'inherit',
          }}
        >
          OUR STORY
        </button>
        <div style={{ marginTop: 6, height: 2.5, width: '100%', background: '#3e3e3d' }} />
      </div>

      {open && (
        <div
          onClick={handleClose}
          style={{
            position: 'fixed',
            inset: 0,
            zIndex: 1000,
            background: visible ? 'rgba(30, 90, 200, 0.3)' : 'rgba(30, 0, 0, 0)',
            backdropFilter: visible ? 'blur(10px)' : 'blur(0px)',
            transition: 'background 0.6s ease, backdrop-filter 0.6s ease',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer',
            overflow: 'auto',
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              maxWidth: 720,
              padding: '60px 40px',
              color: 'white',
              cursor: 'default',
            }}
          >
            <h1
              style={{
                fontSize: 'clamp(2rem, 5vw, 4rem)',
                fontWeight: 800,
                marginBottom: 40,
                opacity: visible ? 1 : 0,
                transform: visible ? 'translateY(0)' : 'translateY(30px)',
                transition: 'opacity 0.8s ease, transform 0.8s ease',
              }}
            >
              Our Story
            </h1>
            {storyParagraphs.map((text, i) => (
              <p
                key={i}
                style={{
                  fontSize: 'clamp(0.9rem, 1.8vw, 1.1rem)',
                  lineHeight: 1.8,
                  marginBottom: 24,
                  opacity: visible ? 1 : 0,
                  transform: visible ? 'translateY(0)' : 'translateY(30px)',
                  transition: `opacity 0.8s ease ${0.15 + i * 0.15}s, transform 0.8s ease ${0.15 + i * 0.15}s`,
                }}
              >
                {text}
              </p>
            ))}
            <div
              style={{
                marginTop: 40,
                opacity: visible ? 0.5 : 0,
                transition: `opacity 0.8s ease ${0.15 + storyParagraphs.length * 0.15}s`,
                fontSize: '13px',
                letterSpacing: 1,
              }}
            >
              CLICK ANYWHERE TO CLOSE
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export function Overlay() {
    return (
      <>
        <div className="char" style={{ top: 40, left: 40, color:'white'}}>
          T
        </div>
        <div className="char" style={{ top: 40, left: '20vw', color:'white' }}>
          U
        </div>
        <div className="char" style={{ top: 40, left: '40vw', color:'white' }}>
          N
        </div>
        <div className="char" style={{ top: '20vw', left: '20vw', color:'white' }}>
          V
        </div>
        <div className="char" style={{ bottom: 40, left: '40vw', color:'white' }}>
          R
        </div>
        <div className="char" style={{ bottom: 40, left: '60vw', color:'white' }}>
          S
        </div>
        <div style={{ position: 'absolute', top: 40, right: 160, fontSize: '15px', textAlign: 'right', color:'white' }}>
          VR SOLUTIONS
          <br />
          ARTIFICIAL INTELLIGENCE
        </div>
        <div style={{ position: 'absolute', top: 40, right: 40, fontSize: '15px', textAlign: 'right', fontVariantNumeric: 'tabular-nums', color:'white' }}>
          —
          <br />
          {new Date().toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric' })}
        </div>
        <svg style={{ position: 'absolute', right: 40, top: '50%' }} width="54" height="23" viewBox="0 0 54 23" fill="none" xmlns="http://www.w3.org/2000/svg">
          <line y1="1.5" x2="54" y2="1.5" stroke="black" strokeWidth="3" />
          <line y1="11.5" x2="54" y2="11.5" stroke="black" strokeWidth="3" />
          <line y1="21.5" x2="54" y2="21.5" stroke="black" strokeWidth="3" />
        </svg>
        <div style={{ position: 'absolute', bottom: 120, left: 120, fontSize: '18px', color:'white' }}>
          Welcome to a new world of possibilities,
          <br />
          take the time to explore...
          <br />
          <br />
          <OurStory />
          <br />
        </div>
        <AudioToggle />
        <ChatAssistant />
      </>
    )
  }
